import LogoLight from "@/assets/insight-light.svg";
import LogoDark from "@/assets/insight-dark.svg";
import { ReportIcon } from "@/components/icons/ReportIcon";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ThemeSwitch from "@/components/ui/ThemeSwitch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronsDown, ChevronsUp, Copy, Edit, Eye, EyeOff, Maximize, Minimize, Plus, Star, StarOff, Trash2 } from "lucide-react";
import React, { memo, useEffect, useState } from "react";
import { toast } from "sonner";
import { useDashboard as useDashboardContext, useDashboardData } from "../contexts/DashboardContext";
import { useDashboard } from "../hooks/api/useDashboard";
import { getAppConfig, setHeaderVisible } from "../services/AppConfigService";
import { GetDashboards, SaveDashboards, SetFavorite } from "../services/DashboardService";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface HeaderProps {
  title: string;
  onAddWidget?: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const Logo = memo(() => (
  <div className="flex items-center gap-4 min-w-[180px]">
    <img
      src={LogoLight}
      alt="TeamsCX Insight"
      className="block dark:hidden"
    />
    <img
      src={LogoDark}
      alt="TeamsCX Insight"
      className="hidden dark:block"
    />
  </div>
));

const TimeDisplay = memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="flex items-center gap-1 hidden xl:flex" role="timer" aria-live="polite">
      <span className="text-sm text-gray-500 dark:text-white hidden xl:inline-block">
        {currentTime.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          hourCycle: 'h23',
        })}
      </span>
    </div>
  );
});

const DefaultGeneralCard: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const { createDashboard } = useDashboard();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { setCurrent, current } = useDashboardData();

  const handleCreateDashboard = async () => {
    try {
      await createDashboard.mutateAsync({ dashboardName: newDashboardName });
      setIsCreateModalOpen(false);
      setNewDashboardName("");
      toast.success("Dashboard created successfully");
    } catch (error) {
      toast.error("Failed to create dashboard");
    }
  };

  useEffect(() => {
    if (open) {
      setDashboards(GetDashboards());
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  function handleFavorite(id: number) {
    SetFavorite(id);
    setDashboards(GetDashboards());
  }
  function handleEdit(id: number) {
    // ...edit logic...
  }
  function handleDelete(id: number) {
    // ...delete logic...
  }

  return (
    <div className="relative">
      {/* <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={buttonRef}
              className="flex items-center gap-2 bg-[#F4F4F4] dark:bg-[#0C3451] rounded-md px-3 py-1 w-fit max-w-[180px] shadow-sm focus:outline-none"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={open}
            >
              <span className="flex items-center justify-center bg-white dark:bg-[#3C5C73] rounded-full p-1">
                <LayoutDashboard size={16} className="text-[#0C3451] dark:text-[#F4F4F4]" />
              </span>
              <span className="font-medium text-[#0C3451] dark:text-[#F4F4F4] text-xs truncate max-w-[100px]">
                {current?.name || "Dashboard"}
              </span>
              <ChevronRight
                size={14}
                className={`text-[#0C3451] dark:text-[#F4F4F4] transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent className="text-xs">{current?.name || "Dashboard"}</TooltipContent>
        </Tooltip>
      </TooltipProvider> */}
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 bg-white dark:bg-[#0C3451] rounded-md shadow-lg z-[60] border border-gray-200 dark:border-[#3C5C73] min-w-max max-w-xs"
          style={{ width: "280px" }}
        >
          {/* Actions Section */}
          <div className="px-4 py-2 border-b border-gray-100 dark:border-[#3C5C73]">
            <div className="text-[10px] font-bold text-[#9DADB9] dark:text-[#F4F4F4] uppercase mb-2">Actions</div>
            <button className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-[#3C5C73] text-[#0C3451] dark:text-[#F4F4F4] text-[11px] font-medium">
              <Eye size={14} className="text-[#0C3451] dark:text-[#F4F4F4]" /> Preview
            </button>
            <button className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-[#3C5C73] text-[#0C3451] dark:text-[#F4F4F4] text-[11px] font-medium">
              <Edit size={14} className="text-[#0C3451] dark:text-[#F4F4F4]" /> Rename
            </button>
            <button
              onClick={() => {
                setIsCreateModalOpen(true);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-[#3C5C73] text-[#0C3451] dark:text-[#F4F4F4] text-[11px] font-medium"
            >
              <Plus size={14} className="text-[#0C3451] dark:text-[#F4F4F4]" /> Create new
            </button>
            <button className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-[#3C5C73] text-[#0C3451] dark:text-[#F4F4F4] text-[11px] font-medium">
              <Copy size={14} className="text-[#0C3451] dark:text-[#F4F4F4]" /> Duplicate
            </button>
          </div>
          {/* Switch to Section */}
          <div className="px-4 py-2">
            <div className="text-[10px] font-bold text-[#9DADB9] dark:text-[#F4F4F4] uppercase mb-2">Switch to</div>
            {dashboards.map((db, idx) => (
              <TooltipProvider key={db.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={
                        db.id === (current?.id)
                          ? "flex items-center gap-2 px-2 py-1.5 rounded text-[#9DADB9] dark:text-[#F4F4F4] text-[11px] font-bold cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis hover:bg-gray-50 dark:hover:bg-[#3C5C73]"
                          : "flex items-center gap-2 px-2 py-1.5 rounded whitespace-nowrap overflow-hidden text-ellipsis text-[11px] dark:text-[#F4F4F4] font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-[#3C5C73]"
                      }
                      style={{ maxWidth: "100%" }}
                      onClick={() => {
                        if (db.id !== current?.id) {
                          setCurrent(db.id);
                          setOpen(false);
                        }
                      }}
                    >
                      <span className="truncate">{db.name}{db.id === (current?.id) ? " (current)" : ""}</span>
                      {db.isFavorite ? (
                        <Star
                          size={12}
                          className="text-[#FFC107] ml-auto"
                          fill="#FFC107"
                          onClick={e => {
                            e.stopPropagation();
                            handleFavorite(db.id);
                          }}
                        />
                      ) : (
                        <StarOff
                          size={12}
                          className="text-[#9DADB9] ml-auto"
                          onClick={e => {
                            e.stopPropagation();
                            handleFavorite(db.id);
                          }}
                        />
                      )}
                      <Edit
                        size={12}
                        className="text-[#9DADB9]"
                        onClick={e => {
                          e.stopPropagation();
                          handleEdit(db.id);
                        }}
                      />
                      <Trash2
                        size={12}
                        className={`text-[#F44336]${idx === 0 ? " opacity-40 cursor-not-allowed" : ""}`}
                        onClick={e => {
                          if (idx === 0) return;
                          e.stopPropagation();
                          handleDelete(db.id);
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">{db.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">New New Dashboard</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="dashboardName" className="text-sm font-medium">
                Dashboard Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dashboardName"
                placeholder="Input the name for the dashboard"
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setNewDashboardName("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateDashboard} disabled={!newDashboardName.trim() || createDashboard.isPending}>
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const HiddenTabsButton: React.FC<{ hiddenTabs: any[]; onUnhide: (tabName: string) => void }> = ({ hiddenTabs, onUnhide }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={buttonRef}
              className="flex items-center justify-center bg-[#F4F4F4] dark:bg-[#0C3451] rounded-md p-2"
              onClick={() => setOpen((v) => !v)}
            >
              <EyeOff size={18} className="text-[#0C3451] dark:text-[#F4F4F4]" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="text-xs">Show hidden tabs</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 bg-white dark:bg-[#0C3451] rounded-md shadow-lg z-[70] border border-gray-200 dark:border-[#3C5C73] min-w-max"
        >
          <div className="px-4 py-2">
            <div className="text-[10px] font-bold text-[#9DADB9] dark:text-[#F4F4F4] uppercase mb-2">Hidden Tabs</div>
            {hiddenTabs.map(tab => (
              <div
                key={tab.name}
                className="px-2 py-1.5 rounded whitespace-nowrap text-[11px] font-medium cursor-pointer hover:bg-gray-50 dark:text-[#F4F4F4] dark:hover:bg-[#3C5C73]"
                onClick={() => {
                  onUnhide(tab.name);
                  setOpen(false);
                }}
              >
                <span className="truncate">{tab.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ title, onAddWidget, activeTab, onTabChange }) => {
  const { theme, setTheme } = useDashboardContext();
  const [isFullscreen, setIsFullscreenState] = useState(false);
  const [headerVisible, setHeaderVisibleState] = useState(getAppConfig().headerVisible);
  const { current } = useDashboardData();
  const [tabsState, setTabsState] = useState<any[]>([]);
  useEffect(() => {
    if (current && current.tabs) {
      setTabsState(current.tabs.map((tab: any) => ({ ...tab })));
    }
  }, [current]);

  // Generate tabs from current dashboard's tabs (hidden = false)
  const tabs = React.useMemo(() => {
    if (!tabsState.length) return [];
    return tabsState
      .filter((tab: any) => !tab.hidden)
      .map((tab: any) => ({
        value: tab.name,
        label: tab.name,
        icon: <ReportIcon className="h-4 w-4" />,
      }));
  }, [tabsState]);

  // Hidden tabs
  const hiddenTabs = React.useMemo(() => {
    if (!tabsState.length) return [];
    return tabsState.filter((tab: any) => tab.hidden);
  }, [tabsState]);

  // Unhide tab handler
  function handleUnhideTab(tabName: string) {
    setTabsState(prev => {
      const updated = prev.map(tab =>
        tab.name === tabName ? { ...tab, hidden: false } : tab
      );
      // Save to dashboard
      if (current) {
        const dashboards = GetDashboards();
        const idx = dashboards.findIndex((d: any) => d.id === current.id);
        if (idx !== -1) {
          dashboards[idx] = { ...dashboards[idx], tabs: updated };
          SaveDashboards(dashboards);
        }
      }
      return updated;
    });
    if (!activeTab) {
      onTabChange(tabName);
    }
  }

  // Sync fullscreen/headerVisible state with AppConfigService
  useEffect(() => {
    const config = getAppConfig();
    setHeaderVisibleState(config.headerVisible);

    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreenState(isFs);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
        .then(() => {
          setIsFullscreenState(true);
        })
        .catch(err => console.error("Failed to go fullscreen:", err));
    } else {
      document.exitFullscreen()
        .then(() => {
          setIsFullscreenState(false);
        })
        .catch(err => console.error("Failed to exit fullscreen:", err));
    }
  };

  const handleShowHeader = () => {
    setHeaderVisibleState(true);
    setHeaderVisible(true);
  };

  const handleHideHeader = () => {
    setHeaderVisibleState(false);
    setHeaderVisible(false);
  };

  return (
    <>
      {!headerVisible && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleShowHeader}
                className="fixed top-0 left-1/2 -translate-x-1/2 z-[60] bg-gray-100 hover:bg-gray-200 dark:hover:bg-[#3C5C73] dark:bg-[#0C3451] dark:text-white rounded"
              >
                <ChevronsDown size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Show header</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {headerVisible && (
        <div className="h-16 px-6 flex items-center justify-between bg-white dark:bg-[#3C5C73] border-b border-[#F4F4F4] dark:border-[#3C5C73] relative">
          <div className="flex gap-3 items-center justify-center">
            <Logo />
            <Tabs value={activeTab} onValueChange={onTabChange} className="flex items-center min-w-0 md:min-w-[400px]">
              <TabsList className="grid grid-cols-4 gap-1 md:gap-3 bg-transparent p-1">
                {tabs.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className={`px-2 flex items-center justify-center rounded-md h-8 font-medium transition-all duration-200
                      data-[state=active]:bg-[#00FEC1] data-[state=active]:text-black rounded-sm
                      data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:bg-[#0C3451] data-[state=inactive]:dark:text-white
                    `}
                    aria-label={`Switch to ${t.label} view`}
                  >
                    {/* <span className="hidden lg:inline mr-2">{t.icon}</span> */}
                    <span className="md:inline text-[12px] lg:text-sm mt-1 md:mt-0">{t.label}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Eye
                              size={16}
                              className="ml-2 cursor-pointer text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white"
                              onClick={e => {
                                e.stopPropagation();
                                setTabsState(prev => {
                                  const updated = prev.map(tab =>
                                    tab.name === t.label ? { ...tab, hidden: true } : tab
                                  );
                                  // Save to dashboard
                                  if (current) {
                                    const dashboards = GetDashboards();
                                    const idx = dashboards.findIndex((d: any) => d.id === current.id);
                                    if (idx !== -1) {
                                      dashboards[idx] = { ...dashboards[idx], tabs: updated };
                                      SaveDashboards(dashboards);
                                    }
                                  }
                                  return updated;
                                });
                                // Optionally, switch to another tab if the current one is hidden
                                if (activeTab === t.value) {
                                  const nextTab = tabs.find(tab => tab.value !== t.value);
                                  if (nextTab)
                                    onTabChange(nextTab.value);
                                  else
                                    onTabChange(""); // Clear active tab if no other tab is available
                                }
                              }}
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">Hide tab</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-4">
            <DefaultGeneralCard />
            {hiddenTabs.length > 0 && (
              <HiddenTabsButton hiddenTabs={hiddenTabs} onUnhide={handleUnhideTab} />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleFullscreen}
                    className="p-2 rounded bg-gray-100 hover:bg-gray-200 dark:hover:bg-[#3C5C73] dark:bg-[#0C3451] dark:text-white"
                  >
                    {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Toggle fullscreen</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <ThemeSwitch checked={theme === "dark"} onChange={toggleTheme} />
            <TimeDisplay />
          </div>
          <button
            onClick={handleHideHeader}
            className="absolute left-1/2 -bottom-2 -translate-x-1/2 z-50 rounded bg-gray-100 hover:bg-gray-200 dark:hover:bg-[#3C5C73] dark:bg-[#0C3451] dark:text-white"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <ChevronsUp size={16} />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Hide header</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      )}
    </>
  );
};

export default Header;
